import React, { useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack } from "@mui/material";
import { X } from "@phosphor-icons/react";
import { ANUploader } from "@answersnow/pkg-theme";
import { useSelector } from "react-redux";
import { useQueryClient } from "react-query";

import { uploadCustomerDocumentAsync } from "api/customers";
import { patchV3ClientAssessment } from "api/assessments";
import { GET_V3_CLIENT_ASSESSMENTS } from "@constants/reactQueryKeys";
import { postUploadFiles } from "hooks/postUploadFiles";
import * as selectors from "selectors";
import type { AssessmentType } from "../types";
import { space3, space5 } from "../styled";

interface AddAssessmentDocumentModalProps {
  /** Human-readable document label, used in the dialog title and uploader. */
  documentLabel: string;
  /** The document type key sent to the document library (e.g. "VB_MAPP_BARRIERS_GRID"). */
  documentType: string;
  /** Client ID used for S3 upload and document registration. */
  clientId: string | number;
  /** The assessment ID to PATCH after upload. */
  assessmentId: string | number;
  /** The assessment type, used to route the PATCH to the correct API segment. */
  assessmentType: AssessmentType;
  /** The camelCase field name to set on the assessment (e.g. "barriersGridDocumentId"). */
  documentFieldName: string;
  onClose: () => void;
}

/**
 * Generic modal for uploading an assessment-associated document (PNG, JPG, or PDF).
 * Used for assessment types that do not support manual data entry (VB-MAPP Barriers,
 * VB-MAPP Transition, Socially Savvy).
 */
export default function AddAssessmentDocumentModal({
  documentLabel,
  documentType,
  clientId,
  assessmentId,
  assessmentType,
  documentFieldName,
  onClose,
}: AddAssessmentDocumentModalProps) {
  const queryClient = useQueryClient();
  const customerDetails = useSelector(selectors.getCustomerDetails);
  const currentUser = useSelector(selectors.getUser);
  const normalizedClientId = String(clientId);

  const [uploadedFiles, setUploadedFiles] = useState<{ file: File; errors: boolean }[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  async function handleUpload() {
    const fileWrapper = uploadedFiles[0];
    const cognitoId: string | undefined = customerDetails?.details?.cognito_id;
    const userId: number | undefined = currentUser?.id;

    if (!fileWrapper || !cognitoId || !userId) {
      onClose();
      return;
    }

    setIsUploading(true);
    try {
      const { success: s3Key, errors } = await postUploadFiles(cognitoId, [
        { file: fileWrapper.file },
      ]);
      if (!errors && s3Key) {
        const uploadResponse = await uploadCustomerDocumentAsync({
          filename: s3Key.split("/").slice(1).join("/"),
          key: s3Key,
          display_filename: fileWrapper.file.name,
          clientId: normalizedClientId,
          type: documentType,
          uploaded_by: userId,
          file_size: fileWrapper.file.size,
        });
        const documentId: number | undefined = uploadResponse?.data?.document_id;
        if (documentId != null) {
          await patchV3ClientAssessment(assessmentType, assessmentId, {
            [documentFieldName]: documentId,
          });
        }
        queryClient.invalidateQueries([GET_V3_CLIENT_ASSESSMENTS, normalizedClientId]);
      }
    } finally {
      setIsUploading(false);
      onClose();
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: space3,
        }}
      >
        Add {documentLabel}
        <IconButton onClick={onClose} size="small" aria-label="Close dialog">
          <X weight="regular" size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack rowGap={space5}>
          <Box>
            <ANUploader
              dropzoneFiles={uploadedFiles}
              alreadyUploadedFiles={[]}
              handleFileUpload={({
                acceptedFiles,
                fileRejections,
              }: {
                acceptedFiles: File[];
                fileRejections: any[];
              }) =>
                setUploadedFiles(
                  [
                    ...acceptedFiles.map((f) => ({ file: f, errors: false })),
                    ...(fileRejections ?? []).map((f) => ({ file: f.file, errors: true })),
                  ].slice(0, 1)
                )
              }
              handleFileDelete={() => setUploadedFiles([])}
              handleLoadedFileDelete={() => {}}
              maxFileSize={3 * 1024 * 1024}
              useDropzoneOptions={{
                accept: {
                  "image/png": [],
                  "image/jpeg": [],
                  "application/pdf": [],
                },
                multiple: false,
              }}
            />
          </Box>

          <Stack direction="row" justifyContent="flex-end" columnGap={space3}>
            <Button variant="contained" color="secondary" onClick={onClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={uploadedFiles.length === 0 || isUploading}
            >
              Upload
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
